import React from 'react';
import { Form, Formik } from 'formik';
import styled from 'styled-components/macro';
import * as Yup from 'yup';

// components
import { CheckBoxField } from 'components/formik/CheckboxField';
import { FormGroup, Label } from 'components/inputs/TextInput';
import { TextField } from 'components/formik/TextField';
// styles
import { Gap } from '../layout';
// utils
import { PHONE_REGEX } from 'utils/regexes';
// queries
import { useCreateConversations } from 'queries/conversations/createConversationMutation';
// types
import { ICreateConversationParams } from 'queries/conversations/types';
import { Button } from 'react-covideo-common';

const BtnWrapper = styled.div`
  width: 205px;
`;

interface IStartConversationParams extends ICreateConversationParams {}

interface IStartConversationForm {
  userId: number;
  receiver: { phone: string | null; email: string | null };
}

const SingleConversationFormSchema = Yup.object().shape({
  email: Yup.string().when('emailPreferred', {
    is: (emailPreferred: boolean[]) => emailPreferred,
    then: () =>
      Yup.string().required().email('Invalid email').required('Required'),
  }),
  phoneNumber: Yup.string().when('smsPreferred', {
    is: (smsPreferred: boolean) => smsPreferred,
    then: () =>
      Yup.string()
        .matches(PHONE_REGEX, 'Phone is not val')
        .required('Required'),
  }),
});

export const StartConversationForm = ({
  userId,
  receiver,
}: IStartConversationForm) => {
  const { mutateAsync: createConversationMutation } = useCreateConversations();

  const INITIAL_VALUES: ICreateConversationParams = {
    email: receiver.email ? receiver.email : '',
    phoneNumber: receiver.phone ? receiver.phone : '',
    smsPreferred: !!receiver.phone,
    emailPreferred: !!receiver.email,
    userId: userId,
  };

  return (
    <Formik
      validationSchema={SingleConversationFormSchema}
      initialValues={INITIAL_VALUES}
      onSubmit={async (values: IStartConversationParams) => {
        createConversationMutation({ data: values });
      }}
    >
      {({ values, submitForm, errors }) => (
        <Form>
          <Gap direction='column'>
            <TextField
              label='Your email address'
              name='email'
              type='email'
              placeholder='Enter email'
              shouldBeTouched={false}
            />
            <TextField
              label='Your phone number'
              name='phoneNumber'
              type='text'
              placeholder='Enter phone'
              shouldBeTouched={false}
            />
            <FormGroup>
              <Label>Preferred Way to Contact</Label>
              <Gap direction='row' gap='40px'>
                <CheckBoxField
                  name='smsPreferred'
                  label='Phone'
                  value='smsPreferred'
                  selected={values.smsPreferred}
                />
                <CheckBoxField
                  name='emailPreferred'
                  label='Email'
                  value='emailPreferred'
                  selected={values.emailPreferred}
                />
              </Gap>
            </FormGroup>
            <BtnWrapper>
              <Button
                text='Start Live Conversation'
                disabled={
                  !!Object.keys(errors).length ||
                  !(values.emailPreferred || values.smsPreferred)
                }
                onClick={submitForm}
              />
            </BtnWrapper>
          </Gap>
        </Form>
      )}
    </Formik>
  );
};
